import { Flex, SegmentedControl, Select, Slider, Switch } from '@byecode/ui'
import type { CustomViewLayout } from '@lighthouse/core'
import { ARRANGEMENT, CUSTOM_VIEW_LAYOUT, DIRECTION } from '@lighthouse/core'
import { Group4ByecodeUi, ListItem4ByecodeUi } from '@lighthouse/shared'
import React from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import { InheritLabel } from '@/components/InheritLabel'
import { GapSizeInput } from '@/components/StyleSetting/AutoLayoutSetting/GapSizeInput'
import { PaddingInput } from '@/components/StyleSetting/AutoLayoutSetting/PaddingInput'
import { useSetExtendsKeys } from '@/components/StyleSetting/hooks/useMouseEventDistance'

import { BLOCK_SIZE_OPTIONS, CUSTOM_VIEW_LAYOUT_CONTROL, DIRECTION_OPTIONS } from '../../../constants'

const CUSTOM_VIEW_RECORD_SIZE = BLOCK_SIZE_OPTIONS.filter(item => item.value !== 'xs')

const COLS_OPTIONS = Array.from({ length: 12 }).map((_, i) => ({
    label: `${i + 1} 列`,
    value: (i + 1).toString()
}))

type Configure = {
    direction?: boolean
    arrangement?: boolean
    size?: boolean
    gap?: boolean
    padding?: boolean
    childIndent?: boolean
    adaptive?: boolean
}

const CONFIGURE: Record<CustomViewLayout, Configure> = {
    [CUSTOM_VIEW_LAYOUT.grid]: {
        direction: true,
        arrangement: true,
        size: true,
        gap: true,
        padding: true
    },
    [CUSTOM_VIEW_LAYOUT.carousel]: {
        padding: true
    },
    [CUSTOM_VIEW_LAYOUT.waterfall]: {
        arrangement: true,
        size: true,
        gap: true,
        padding: true
    },
    [CUSTOM_VIEW_LAYOUT.weekCalendar]: {
        adaptive: true,
        size: true,
        gap: true,
        padding: true
    },
    [CUSTOM_VIEW_LAYOUT.monthCalendar]: {
        adaptive: true,
        size: true,
        gap: true,
        padding: true
    },
    [CUSTOM_VIEW_LAYOUT.comment]: {
        gap: true,
        padding: true,
        childIndent: true
    },
    [CUSTOM_VIEW_LAYOUT.tree]: {
        gap: true,
        padding: true
    },
    [CUSTOM_VIEW_LAYOUT.multiDimensional]: {
        size: true,
        gap: true,
        padding: true
    }
}

export const View: React.FC = () => {
    const { control, getValues } = useFormContext /* <ViewBreakPointProtocol & PaginationProtocol> */()
    const { handleSetBreakKey } = useSetExtendsKeys()
    const [width, layout, direction, arrangement] = getValues([
        'breakPoint.size.width',
        'breakPoint.viewLayout.layout',
        'breakPoint.viewLayout.direction',
        'breakPoint.viewLayout.arrangement'
    ])

    const isAutoWidth = width?.size === 'auto'
    const viewLayout = (layout || CUSTOM_VIEW_LAYOUT.grid) as CustomViewLayout
    const isWrappedHorizontalGrid = viewLayout === CUSTOM_VIEW_LAYOUT.grid && direction === DIRECTION.horizontal
    const adaptive = useWatch({ control, name: 'breakPoint.viewLayout.adaptive' })

    return (
        <Group4ByecodeUi label="视图布局">
            {CONFIGURE[viewLayout]?.adaptive && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="自适应宽度" name="breakPoint.viewLayout.adaptive" />
                    <Controller
                        control={control}
                        name="breakPoint.viewLayout.adaptive"
                        render={({ field }) => (
                            <Switch
                                checked={field.value ?? false}
                                onChange={e => {
                                    field.onChange(e.target.checked)
                                    handleSetBreakKey('breakPoint.viewLayout.adaptive')
                                }}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}

            {CONFIGURE[viewLayout]?.direction && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="方向" name="breakPoint.viewLayout.direction" />
                    <Controller
                        control={control}
                        name="breakPoint.viewLayout.direction"
                        render={({ field }) => (
                            <SegmentedControl
                                style={{ width: 180 }}
                                fullWidth
                                data={DIRECTION_OPTIONS}
                                value={field.value}
                                onChange={val => {
                                    field.onChange(val)
                                    handleSetBreakKey('breakPoint.viewLayout.direction')
                                }}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}

            {isWrappedHorizontalGrid && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="换行" name="breakPoint.viewLayout.wrap" />
                    <Controller
                        control={control}
                        name="breakPoint.viewLayout.wrap"
                        render={({ field }) => (
                            <Switch
                                checked={field.value ?? false}
                                onChange={event => {
                                    field.onChange(event.target.checked)
                                    handleSetBreakKey('breakPoint.viewLayout.wrap')
                                }}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}

            {!isAutoWidth && CONFIGURE[viewLayout]?.arrangement && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="排列方式" name="breakPoint.viewLayout.arrangement" />
                    <Controller
                        control={control}
                        name="breakPoint.viewLayout.arrangement"
                        render={({ field }) => (
                            <SegmentedControl
                                style={{ width: 180 }}
                                fullWidth
                                data={CUSTOM_VIEW_LAYOUT_CONTROL}
                                value={field.value}
                                onChange={val => {
                                    field.onChange(val)
                                    handleSetBreakKey('breakPoint.viewLayout.arrangement')
                                }}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}

            {/* 如果宽度是适应内容，则隐藏每行显示和尺寸的配置 */}
            {!isAutoWidth &&
                !adaptive &&
                CONFIGURE[viewLayout]?.size &&
                arrangement !== ARRANGEMENT.fit &&
                (arrangement === ARRANGEMENT.fixed ? (
                    <ListItem4ByecodeUi key="cols" justifyContent="space-between" alignItems="center">
                        <InheritLabel label="每行显示" name="breakPoint.viewLayout.cols" />
                        <Controller
                            control={control}
                            name="breakPoint.viewLayout.cols"
                            shouldUnregister={false}
                            render={({ field: { value, onChange } }) => (
                                <Select
                                    value={value?.toString()}
                                    style={{ width: 180 }}
                                    searchable={false}
                                    options={COLS_OPTIONS}
                                    onChange={val => {
                                        onChange(Number(val))
                                        handleSetBreakKey('breakPoint.viewLayout.cols')
                                    }}
                                />
                            )}
                        />
                    </ListItem4ByecodeUi>
                ) : (
                    <ListItem4ByecodeUi key="size" justifyContent="space-between" alignItems="center">
                        <InheritLabel label="尺寸" name="breakPoint.viewLayout.size" />
                        <Controller
                            control={control}
                            name="breakPoint.viewLayout.size"
                            shouldUnregister={false}
                            render={({ field }) => (
                                <SegmentedControl
                                    size="md"
                                    fullWidth
                                    data={CUSTOM_VIEW_RECORD_SIZE}
                                    style={{ width: 180 }}
                                    value={field.value}
                                    onChange={val => {
                                        field.onChange(val)
                                        handleSetBreakKey('breakPoint.viewLayout.size')
                                    }}
                                />
                            )}
                        />
                    </ListItem4ByecodeUi>
                ))}

            {CONFIGURE[viewLayout]?.gap && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="间距" name="breakPoint.viewLayout.gap" />
                    <Controller
                        name="breakPoint.viewLayout.gap"
                        control={control}
                        render={({ field }) => (
                            <GapSizeInput
                                value={field.value}
                                onChange={val => {
                                    field.onChange(val)
                                    handleSetBreakKey('breakPoint.viewLayout.gap')
                                }}
                                direction={direction || DIRECTION.vertical}
                                disableHighlight
                                disablePercent
                                disableRem
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}

            {CONFIGURE[viewLayout]?.padding && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="flex-start">
                    <Flex alignItems="center" style={{ height: 32 }}>
                        <InheritLabel label="边距" name="breakPoint.viewLayout.viewPadding" />
                    </Flex>
                    <Controller
                        name="breakPoint.viewLayout.viewPadding"
                        control={control}
                        render={({ field }) => (
                            <PaddingInput
                                value={field.value}
                                onChange={val => {
                                    field.onChange(val)
                                    handleSetBreakKey('breakPoint.viewLayout.viewPadding')
                                }}
                                disableHighlight
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}

            {CONFIGURE[viewLayout]?.childIndent && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="flex-start">
                    <InheritLabel label="子级缩进" name="breakPoint.viewLayout.childIndent" />
                    <Controller
                        name="breakPoint.viewLayout.childIndent"
                        control={control}
                        render={({ field }) => (
                            <Slider
                                step={1}
                                min={0}
                                max={500}
                                defaultValue={24}
                                value={field.value}
                                onChange={val => {
                                    field.onChange(val)
                                    handleSetBreakKey('breakPoint.viewLayout.childIndent')
                                }}
                                styles={{ root: { width: 180 } }}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}
        </Group4ByecodeUi>
    )
}
